/* eslint-disable no-console, curly */
// src/lib/sentry.js
/**
 * Sentry initialization for error tracking
 *
 * This file initializes Sentry when the DSN is available.
 * It should be imported in src/index.js before rendering the app.
 */

const isDevelopment = import.meta.env.DEV;
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

let sentryEnvironment = "production";
if (import.meta.env.VITE_SENTRY_ENVIRONMENT) {
  sentryEnvironment = import.meta.env.VITE_SENTRY_ENVIRONMENT;
} else if (isDevelopment) {
  sentryEnvironment = "development";
}

let enableTracking = !isDevelopment;
if (import.meta.env.VITE_ENABLE_ERROR_TRACKING === "true") {
  enableTracking = true;
}

/**
 * Initialize Sentry if DSN is provided
 */
export function initSentry() {
  // Always log initialization attempt (helps with debugging)
  // eslint-disable-next-line no-console
  console.log("=== SENTRY INITIALIZATION START ===");
  // eslint-disable-next-line no-console
  console.log("[Sentry] Initialization check", {
    hasDsn: !!sentryDsn,
    dsnPreview: sentryDsn ? `${sentryDsn.substring(0, 20)}...` : "not set",
    enableTracking,
    environment: sentryEnvironment,
    nodeEnv: import.meta.env.MODE,
    allEnvVars: {
      VITE_SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN ? "SET" : "NOT SET",
      VITE_SENTRY_ENVIRONMENT: import.meta.env.VITE_SENTRY_ENVIRONMENT || "not set",
      VITE_ENABLE_ERROR_TRACKING: import.meta.env.VITE_ENABLE_ERROR_TRACKING || "not set",
    },
  });

  // Only initialize if DSN is provided and tracking is enabled
  if (!sentryDsn || !enableTracking) {
    // eslint-disable-next-line no-console
    console.warn("[Sentry] Not initialized - DSN not provided or tracking disabled", {
      hasDsn: !!sentryDsn,
      enableTracking,
    });
    return false;
  }

  try {
    // Dynamic import to avoid errors if @sentry/react is not installed
    import("@sentry/react")
      .then((SentryModule) => {
        const Sentry = SentryModule.default || SentryModule;
        let tracesRate = 0.1;
        let replayRate = 0.1;
        if (isDevelopment) {
          tracesRate = 1.0;
          replayRate = 1.0;
        }

        Sentry.init({
          dsn: sentryDsn,
          environment: sentryEnvironment,
          integrations: [
            // Automatically instrument React components
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
              // Capture 10% of all sessions,
              // plus 100% of sessions with an error
              sessionSampleRate: 0.1,
              errorSampleRate: 1.0,
            }),
          ],
          // Performance Monitoring
          tracesSampleRate: tracesRate, // 100% in dev, 10% in prod
          // Session Replay
          replaysSessionSampleRate: replayRate,
          replaysOnErrorSampleRate: 1.0, // Always record errors
          // Release tracking
          release: import.meta.env.VITE_VERSION || undefined,
          // Filter out known non-critical errors
          beforeSend(event, hint) {
            // Don't send errors in development unless explicitly enabled
            if (isDevelopment && import.meta.env.VITE_ENABLE_ERROR_TRACKING !== "true") {
              return null;
            }

            // Filter out browser extension errors
            if (event.exception) {
              const error = hint.originalException || hint.syntheticException;
              if (error) {
                const isExtensionError =
                  error.message?.includes("chrome-extension://") ||
                  error.message?.includes("moz-extension://") ||
                  error.message?.includes("safari-extension://");
                if (isExtensionError) {
                  return null;
                }
              }
            }

            return event;
          },
          // Ignore specific errors
          ignoreErrors: [
            // Browser extensions
            "top.GLOBALS",
            "originalCreateNotification",
            "canvas.contentDocument",
            "MyApp_RemoveAllHighlights",
            "atomicFindClose",
            // Network errors that are handled gracefully
            "NetworkError",
            "Failed to fetch",
            // ResizeObserver errors (common, non-critical)
            "ResizeObserver loop limit exceeded",
            "ResizeObserver loop completed with undelivered notifications",
          ],
        });

        // Set initial tags
        Sentry.setTag("app", "meds-cafe-ops");
        Sentry.setTag("platform", "web");

        // Always log initialization status (helps with debugging)
        // eslint-disable-next-line no-console
        console.log("[Sentry] Initialized successfully", {
          environment: sentryEnvironment,
          dsn: sentryDsn ? `${sentryDsn.substring(0, 20)}...` : "not set",
          trackingEnabled: enableTracking,
        });

        // Expose Sentry on window for debugging
        if (typeof window !== "undefined") {
          window.__SENTRY_INITIALIZED__ = true;
          window.__SENTRY_ENV__ = sentryEnvironment;
        }
      })
      .catch((error) => {
        // @sentry/react not installed - this is okay, errorTracking.js will handle fallback
        // eslint-disable-next-line no-console
        console.error("[Sentry] Failed to initialize:", error);
        if (typeof window !== "undefined") {
          window.__SENTRY_INITIALIZED__ = false;
          window.__SENTRY_ERROR__ = error.message;
        }
      });
  } catch (error) {
    // Log initialization failure
    // eslint-disable-next-line no-console
    console.error("[Sentry] Initialization error:", error);
    if (typeof window !== "undefined") {
      window.__SENTRY_INITIALIZED__ = false;
      window.__SENTRY_ERROR__ = error.message;
    }
  }

  return true;
}

/**
 * Get Sentry instance (for advanced usage)
 */
export function getSentry() {
  if (typeof window !== "undefined" && window.Sentry) {
    return window.Sentry;
  } else {
    return null;
  }
}
